import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { getTicketRequestDetails, isTicketRequest } from '../../util/listingTypes';
import css from './ListingPage.module.css';

/**
 * Component that displays bid-specific details (date, venue, ticket type, etc.)
 * for reverse price negotiation listings (bids).
 */
const SectionBidDetailsMaybe = props => {
  const { listing, intl, listingFieldConfigs } = props;

  if (!listing || !isTicketRequest(listing)) {
    return null;
  }

  const bidDetails = getTicketRequestDetails(listing);
  if (!bidDetails) {
    return null;
  }

  const { eventDate, eventDateTime, ticketType, numberOfTickets, buyersMessage } = bidDetails;

  // Get enum options for ticket_type to find the label and check if it's "specific"
  let ticketTypeLabel = ticketType; // Default to the raw value
  let isSpecificOption = false;
  if (listingFieldConfigs) {
    const ticketTypeFieldConfig = listingFieldConfigs.find(config => config.key === 'ticket_type');
    if (ticketTypeFieldConfig && ticketTypeFieldConfig.enumOptions) {
      const selectedOption = ticketTypeFieldConfig.enumOptions.find(
        opt => opt.option === ticketType || opt.option === String(ticketType)
      );
      if (selectedOption) {
        ticketTypeLabel = selectedOption.label;
        // Check if the label contains "specific" (case-insensitive)
        isSpecificOption = selectedOption.label?.toLowerCase().includes('specific');
      }
    }
  }

  // Also check the raw value if we couldn't determine from the config
  if (!isSpecificOption) {
    isSpecificOption =
      ticketType === 'specific' ||
      String(ticketType)
        .toLowerCase()
        .includes('specific');
  }

  // Format event date & time (prefer eventDateTime, fallback to eventDate)
  const eventDateSource = eventDateTime || eventDate;
  let formattedEventDateTime = null;
  if (eventDateSource) {
    try {
      const date =
        typeof eventDateSource === 'string' ? new Date(eventDateSource) : new Date(eventDateSource);
      if (!isNaN(date.getTime())) {
        const datePart = intl.formatDate(date, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        const timePart = intl.formatTime(date, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        formattedEventDateTime = `${datePart} at ${timePart}`;
      }
    } catch (e) {
      formattedEventDateTime = eventDateSource;
    }
  }

  const seatInformation = isSpecificOption
    ? intl.formatMessage({
        id: 'ListingPage.bidDetails.seatInformationSpecific',
        defaultMessage: 'Specific seating - see details below',
      })
    : intl.formatMessage({
        id: 'ListingPage.bidDetails.seatInformationGeneral',
        defaultMessage: 'Just get me in!',
      });

  // Fallback to selected enum label if value cannot be resolved to a known option.
  const seatInformationText = seatInformation || ticketTypeLabel;
  const ticketsCount = numberOfTickets ? numberOfTickets.toString() : '1';
  const eventDateTimeText =
    formattedEventDateTime ||
    intl.formatMessage({ id: 'ListingPage.bidDetails.eventDateTimeTbd', defaultMessage: 'TBD' });

  return (
    <section className={css.sectionBidDetails}>
      <div className={css.bidDetailBlock}>
        <h3 className={css.bidDetailBlockHeading}>
          <FormattedMessage
            id="ListingPage.bidDetails.seatInformation"
            defaultMessage="Seat information"
          />
        </h3>
        <p className={css.bidDetailBlockValue}>{seatInformationText}</p>
      </div>

      {/* Buyer's message section - only show if message exists */}
      {buyersMessage && (
        <div className={css.bidDetailBlock}>
          <h3 className={css.bidDetailBlockHeading}>
            <FormattedMessage id="ListingPage.buyersMessageTitle" />
          </h3>
          <p className={css.bidDetailBlockValue}>{buyersMessage}</p>
        </div>
      )}

      <div className={css.bidDetailBlock}>
        <h3 className={css.bidDetailBlockHeading}>
          <FormattedMessage id="ListingPage.bidDetails.numberOfTickets" />
        </h3>
        <p className={css.bidDetailBlockValue}>{ticketsCount}</p>
      </div>

      <div className={css.bidDetailBlock}>
        <h3 className={css.bidDetailBlockHeading}>
          <FormattedMessage
            id="ListingPage.bidDetails.eventDateTime"
            defaultMessage="Event date & time"
          />
        </h3>
        <p className={css.bidDetailBlockValue}>{eventDateTimeText}</p>
      </div>
    </section>
  );
};

export default SectionBidDetailsMaybe;
